<template>
  <app-modal
    size="sm"
    @close="emit('close')"
  >
    <template #body>
      <section class="card__padding">
        <h3 class="title__h3 bold">
          {{ t('literal.invite_members_to_project') }}
        </h3>

        <p class="margin__t--6">
          {{ t('project_procedure.invite_members_description') }}
        </p>

        <form-notification
          class="margin__t--24"
          :keys="error"
        />

        <div class="margin__t--24">
          <template
            v-for="(invite, index) in invites"
            :key="`invite_${index}`"
          >
            <procedure-invite-members-item
              class="margin__t--12"
              v-model="invite.email"
            />
          </template>
        </div>

        <div class="margin__t--18">
          <button
            class="btn btn__invite"
            v-if="showAddEmailButton"
            @click.prevent="handleClickAddEmail()"
          >
            <fa-icon
              class="margin__r--6"
              :icon="['fal', 'plus']"
            />

            {{ t('literal.invite_another_member') }}
          </button>
        </div>

        <div class="margin__t--24">
          <app-save-button
            class="btn btn__size--small btn__color--prim"
            :disabled="loader || !emails.length"
            :is-loading="loader"
            @click="handlePostInvites()"
          >
            <template #icon>
              <fa-icon :icon="['fal', 'paper-plane']" />
            </template>

            {{ t('literal.send_invitation') }}
          </app-save-button>
        </div>
      </section>
    </template>
  </app-modal>
</template>

<script setup>
import { ref, computed, defineAsyncComponent, onMounted, onUnmounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useVuelidate } from '@vuelidate/core'
import { useI18n } from '@/vendors/i18n'
import { useProjectStore } from '@/stores/ProjectStore'

import AppSaveButton from '&/molecules/AppSaveButton'
import AppModal from '&/organisms/AppModal'
import ProcedureInviteMembersItem from '&/modules/project/ProcedureInviteMembersItem'
const FormNotification = defineAsyncComponent(() => import('&/molecules/FormNotification'))

const emit = defineEmits(['close'])

const { projectSlug, limit } = defineProps({
  projectSlug: { type: String, required: true },
  limit: { type: Number, default: 0 }
})

const { t } = useI18n()
const { getProject, fetchInvites, postProjectInvites } = useProjectStore()
const { loader, error } = storeToRefs(useProjectStore())
const v$ = useVuelidate(null, null, { $scope: 'invite' })

const { project } = getProject({ id: projectSlug })

const invites = ref([{ email: null }])

const emails = computed(() => invites.value.filter(invite => invite.email))
const showAddEmailButton = computed(() => !project.value?.limit_of_members || invites.value.length < limit)

const handleClickAddEmail = () => invites.value.push({ email: null })

const handlePostInvites = async () => {
  v$.value.$touch()

  if (v$.value.$error) return

  await postProjectInvites({ project_id: project.value.id, invites: emails.value })
  await fetchInvites({ project_id: project.value?.id, query: { active: true, 'sort_by_field[field]': 'created_at', 'sort_by_field[direction]': 'asc' } })
  error.value = null
  emit('close')
}

onMounted(() => error.value = null)
onUnmounted(() => error.value = null)
</script>

<style lang="scss" scoped>
.btn {
  &__invite {
    border: 1px dashed rgba($dg, 0.1);
    display: block;
    width: 100%;
    text-align: left;
    padding: 12px;
    font-size: em(14px);
    color: rgba($dg, 0.6);
    transition: all .3s;

    &:hover {
      border: 1px dashed rgba($dg, 0.6);
      color: rgba($dg, 0.8);
    }
  }
}
</style>
